import { useCallback } from 'react';

import { Button } from '@braskai/ui-kit';

import { ActionButtonTitle } from '@/widgets/pricing/pricingCards/lib/enums';

import { useBalanceStore } from '@/entities/balance';
import { useProducts } from '@/entities/pricing';
import { useCurrentUser } from '@/entities/user/currentUser';

import { RASK_PRICING_CHAT_LINK } from '@/shared/constants/docLinks';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, PlanCategory, Price } from '@/shared/types/Billing';

import { ActionButton } from './styled';
import { getButtonTitle } from './utils/getButtonTitle';
import { getMixpanelButtonEvent } from './utils/getMixpanelButtonEvent';

type CardButtonProps = {
  chosenPlanId: null | string;
  isActiveSubscription: boolean;
  isCustomPlan: boolean;
  isPriceActive: boolean;
  plan: Plan;
  priceId: string | undefined;
  onClick: (priceId: Price['id'], plan: Plan) => void;
};

export const CardButton = ({
  chosenPlanId,
  isActiveSubscription,
  isCustomPlan,
  isPriceActive,
  plan,
  priceId,
  onClick,
}: CardButtonProps) => {
  const {
    hasCanceledSubscription,
    hasExpiredSubscription,
    hasPausedSubscription,
    hasSubscriptionCancelAtPeriodEnd,
    hasTrialingEnterprisePlan,
    hasTrialPlan
  } = useBalanceStore();

  const { products } = useProducts();
  const { data: user } = useCurrentUser();

  const { id: cardPlanId, name: planName } = plan || {};
  const activePrice = plan?.prices.find((p) => p.id === priceId);
  const subscription = activePrice?.recurring?.interval === PaymentInterval.Month ? 'monthly' : 'annual';
  const userPriceData = user?.subscription?.plan;

  const isPlanActive = Boolean(
    userPriceData && userPriceData.product_id === cardPlanId && !hasCanceledSubscription && !hasExpiredSubscription,
  );

  const isPurchaseInProgress = Boolean(chosenPlanId);
  const isCurrentPlanLoading = chosenPlanId === priceId;

  const buttonTitle = getButtonTitle({
    hasCanceledSubscription,
    hasExpiredSubscription,
    hasPausedSubscription,
    hasSubscriptionCancelAtPeriodEnd,
    hasTrialingEnterprisePlan,
    hasTrialPlan,
    isPlanActive,
    isPriceActive,
    plan,
    plans: products?.plan,
    subscription: user?.subscription,
  });

  const isDowngrade = buttonTitle === ActionButtonTitle.Downgrade;
  const isSwitchToAnnualPlan = buttonTitle === ActionButtonTitle.SwitchToAnnual;
  const isUpgrade = buttonTitle === ActionButtonTitle.Upgrade;

  const isCreatorProPlan = plan.category === PlanCategory.CreatorPro;

  const handleGetStartedClick = useCallback(() => {
    if (!priceId) {
      return;
    }

    const sendEvent = getMixpanelButtonEvent({
      category: plan.category,
      isDowngradePlan: isDowngrade,
      isSwitchToAnnualPlan,
      isUpgradePlan: isUpgrade,
      planName,
      subscription,
    });

    sendEvent?.();

    onClick(priceId, plan);
  }, [priceId, plan, onClick, planName, isUpgrade, isSwitchToAnnualPlan, isDowngrade, subscription]);

  const handleEnterpriseLinkClick = () => {
    if (isPurchaseInProgress) {
      return;
    }

    MixpanelEvents.enterpriseButtonClick();
    window.open(RASK_PRICING_CHAT_LINK, '_blank');
  };

  if (isCustomPlan) {
    return (
      <Button data-testid="card-button-contact-us" disabled={isPurchaseInProgress} size="sm" variant="outlined" onClick={handleEnterpriseLinkClick}>
        Contact us
      </Button>
    );
  }

  return (
    <ActionButton
      data-testid="card-button-get-started-click"
      disabled={isPurchaseInProgress}
      isLoading={isCurrentPlanLoading}
      isTransparent={isUpgrade && isActiveSubscription ? false : isDowngrade || !isCreatorProPlan}
      onClick={handleGetStartedClick}
    >
      {buttonTitle}
    </ActionButton>
  );
};
